// A rule rendered as a read-only sentence.
//
// The same phrasing the builder edits, so what an engineer configured and what a reviewer reads
// are visibly the same thing. Used in the rule list, on an asset's Automation panel, and in the
// confirmation dialog before a rule is armed.

import React from "react";
import {
  OPERATORS, CHANNEL_LABELS, describeSource,
} from "../../lib/automationModel";

function Keyword({ children }) {
  return (
    <span className="text-[10px] font-mono uppercase tracking-widest text-gray-400 w-20 shrink-0 text-right pt-1 select-none">
      {children}
    </span>
  );
}

function Pill({ children, tone = "device", title }) {
  const tones = {
    device: "bg-gray-50 border-gray-300 text-gray-900",
    value: "bg-orange-50 border-orange-300 text-orange-800 font-mono tabular-nums",
    op: "bg-white border-gray-200 text-gray-600",
    missing: "bg-red-50 border-red-300 text-red-700 italic",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium whitespace-nowrap ${tones[tone] || tones.device}`}
    >
      {children}
    </span>
  );
}

function Lit({ children }) {
  return <span className="text-xs text-gray-400">{children}</span>;
}

function SourcePill({ source, ctx }) {
  if (!source) return <Pill tone="missing">not set</Pill>;
  const label = describeSource(source, ctx);
  const detail =
    source.kind === "io"
      ? `raw register ${source.io_point_name} on ${source.gateway_id}`
      : source.kind === "asset"
      ? `${CHANNEL_LABELS[source.channel || "primary"]} of this device`
      : undefined;
  return <Pill title={detail}>{label}</Pill>;
}

function ConditionLine({ condition, ctx, keyword }) {
  const op = OPERATORS.find((o) => o.value === condition.op);
  const valueText =
    condition.value === "" || condition.value == null
      ? "…"
      : condition.unit
      ? `${condition.value} ${condition.unit}`
      : String(condition.value) === "1"
      ? "ON"
      : String(condition.value) === "0"
      ? "OFF"
      : String(condition.value);

  return (
    <div className="flex flex-wrap items-center gap-1.5 leading-8">
      <Keyword>{keyword}</Keyword>
      <SourcePill source={condition.source} ctx={ctx} />
      <Pill tone="op">{op?.label || condition.op}</Pill>
      <Pill tone="value">{valueText}</Pill>
      {condition.for_seconds ? (
        <>
          <Lit>for</Lit>
          <Pill tone="value">{condition.for_seconds}s</Pill>
        </>
      ) : null}
    </div>
  );
}

export default function RuleSentence({ rule, ctx, className = "" }) {
  const conditions = rule.trigger?.all || [];
  const permissives = rule.permissives || [];
  const actions = rule.actions || [];

  return (
    <div className={`text-sm ${className}`}>
      {conditions.map((c, i) => (
        <ConditionLine key={`t${i}`} condition={c} ctx={ctx} keyword={i === 0 ? "when" : "and"} />
      ))}

      {permissives.map((c, i) => (
        <ConditionLine key={`p${i}`} condition={c} ctx={ctx} keyword={i === 0 ? "only if" : "and"} />
      ))}

      {actions.map((a, i) => (
        <div key={`a${i}`} className="flex flex-wrap items-center gap-1.5 leading-8">
          <Keyword>{i === 0 ? "then" : "and"}</Keyword>
          {a.type === "write" ? (
            <>
              <Pill tone="op">{String(a.value) === "1" ? "Start" : String(a.value) === "0" ? "Stop" : `Set ${a.value}`}</Pill>
              <SourcePill source={a.target} ctx={ctx} />
            </>
          ) : (
            <>
              <Pill tone="op">raise {a.severity || "warning"} alert</Pill>
              <Pill>{a.title || "untitled"}</Pill>
            </>
          )}
        </div>
      ))}

      {rule.feedback?.source && (
        <div className="flex flex-wrap items-center gap-1.5 leading-8">
          <Keyword>confirm</Keyword>
          <Lit>via</Lit>
          <SourcePill source={rule.feedback.source} ctx={ctx} />
          <Lit>within</Lit>
          <Pill tone="value">{rule.feedback.timeout_s || "?"}s</Pill>
          <Lit>, else</Lit>
          <Pill tone="op">{(rule.feedback.on_fail || "critical_alert").replace(/_/g, " ")}</Pill>
        </div>
      )}

      {rule.reset && (
        <div className="flex flex-wrap items-center gap-1.5 leading-8">
          <Keyword>reset</Keyword>
          <Pill tone="op">{rule.reset.mode === "manual" ? "manually" : "automatically"}</Pill>
          {rule.reset.mode === "auto_on_clear" && rule.reset.hysteresis ? (
            <>
              <Lit>with a</Lit>
              <Pill tone="value">{rule.reset.hysteresis}</Pill>
              <Lit>band</Lit>
            </>
          ) : null}
          {rule.reset.min_off_seconds ? (
            <>
              <Lit>· min off</Lit>
              <Pill tone="value">{rule.reset.min_off_seconds}s</Pill>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
